import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx"
import { addNotification, NotificationTypes } from "../../stores/notification-store.js"
import { checkDeviceDescriptionConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import { UpdateProfile } from "../../utils/api.js";
import { NOT_FOUND } from "../../utils/constants.js"

import useProfile from "../../hooks/useProfile.js"
import useProfileDeviceSchema, { deviceSchema } from "../../hooks/useProfileDeviceSchema.js"
import useProfileSchema, { profileSchema } from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

export default function ProfileDeviceDetail() {

    const navigate = useNavigate();
    const { beId, profileId, name } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const desc = searchParams.get('desc')
    const editMode = !!desc
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { profile, loading: profileLoading, isLoaded: profileIsLoaded, modified, ccschema } = useProfile(beId, profileId, config, accessToken)
    const device = useComputed(() => {
        if (profile.value.componentProfile && editMode) {
            const deviceList = profile.value.componentProfile?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return {}

            const index = profile.value.componentProfile.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            const currentDevice = profile.value.componentProfile.config.deviceConfig.device[index]
            const jsonFormDevice = { config: currentDevice }
            return jsonFormDevice
        }
        return null
    })
    const deviceIndex = useComputed(() => {
        if (profile.value.componentProfile && editMode) {
            const deviceList = profile.value.componentProfile?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return -1

            const index = profile.value.componentProfile.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            return index
        }
        return -1
    })
    const { loading: schemaLoading, isLoaded: schemaIsLoaded, version: profileVersion } = useProfileSchema(beId, ccschema.value, manifest.value, config, accessToken)
    const { loading: deviceSchemaLoading, isLoaded: deviceSchemaIsLoaded } = useProfileDeviceSchema(name, manifest.value, config, accessToken)
    
    
    const pageTitle = useComputed(() => {
        return editMode ?
        `Manage ${name} UTG Device` :
        `Add ${name} UTG Device`
    })
    
    const jsonFormData = useComputed(() => {

        if (isEmptyObject(deviceSchema.value) || isEmptyObject(profile.value)) {
            return
        }

        // deep clone the schema
        let clonedSchema = structuredClone(deviceSchema.value)

        // disable editing of all fields if canEditSignal is false
        if (!canEditSignal.value) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }
        
        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'PROFILE'
        console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
        const formData = {
            schema: clonedSchema.properties.config,
            loadedComponentConfig: profile.value.componentProfile.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditSignal.value
        }
        if(device.value) {
            formData.value = device.value.config
        }
        console.log('formData from profile device config', formData)
        return formData
    }) 

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const isFormSubmitting = useSignal(false)
    const accordionData = [
        { name: 'Device', data: device },
        { name: 'Device Index', data: deviceIndex },
        { name: 'Device Schema', data: deviceSchema },
        { name: 'Component Profile', data: profile },
        { name: 'Access Token', data: accessToken},
        { name: 'App Config', data: config},
        { name: 'Schema Manifest', data: manifest }
    ]
   
    const handleFormSubmit = values => {
        
        console.log('values', values)

        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmitting.value) {
            return false
        }
        isFormSubmitting.value = true

        try {
            console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    id: profile.value.componentProfile.id,
                    name: profile.value.componentProfile.name,
                    businessEntityId: profile.value.componentProfile.businessEntityId,
                    config: profile.value.componentProfile.config,
                }

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.deviceConfig.device[deviceIndex.value] = values
                }
                else {
                    // insert values into the apiInterface array
                    data.config.deviceConfig.device.push(values)
                }
                
                // validate taskDesc uniqueness in case jsonform validation fails
                if (checkDeviceDescriptionConflicts(data.config.deviceConfig.device)) {
                    addNotification("Unable to save due to conflict. All Devices must have a unique Device Name.", NotificationTypes.ERROR)
                    return false
                }

                // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
                data.config.utgGlobal.ccschema = profileVersion.value

                console.log(data)
                UpdateProfile(beId, profileId, data, config, accessToken)
                    .then(result => {
                        modified.value = true
                        addNotification('Devices was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate('../')
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Device.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmitting.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        console.log('handleFormChange')
        isFormEdited.value = true
    }

    if (!profileIsLoaded.value || deviceSchemaLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../devices')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                Profile List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {profile.value.componentProfile.name}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title={pageTitle.value} canEditSignal={canEditSignal} />
            <ProfileTools profile={profile.value}></ProfileTools>
        </Stack>
        <ProfileNav beId={beId} profileId={profile.value.componentProfile.id} activeKey="3" debug={debug}></ProfileNav>

        <JsonForm data={jsonFormData.value}></JsonForm>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}